import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../services/api';
import { toast } from 'sonner';
import { 
  TrendingUp, Users, DollarSign, FileText, Target, BarChart3
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#1B5AFF', '#00C9A7', '#F0B429', '#8B5CF6'];

const AdminAnalytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('12m');

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchAnalytics();
  }, [user, navigate, dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin/analytics?date_range=${dateRange}`);
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Analytics" breadcrumbs={[{ label: 'Analytics' }]}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B5AFF]"></div>
        </div>
      </AdminLayout>
    );
  }

  const dateRangeSelector = (
    <Select value={dateRange} onValueChange={setDateRange}>
      <SelectTrigger className="w-[150px] bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg">
        <SelectValue placeholder="Date Range" />
      </SelectTrigger>
      <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
        <SelectItem value="30d" className="text-[#0A0F1E]">Last 30 Days</SelectItem>
        <SelectItem value="90d" className="text-[#0A0F1E]">Last 90 Days</SelectItem>
        <SelectItem value="12m" className="text-[#0A0F1E]">Last 12 Months</SelectItem>
        <SelectItem value="all" className="text-[#0A0F1E]">All Time</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <AdminLayout 
      title="Analytics" 
      breadcrumbs={[{ label: 'Analytics' }]}
      actions={dateRangeSelector}
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#1B5AFF]/20 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-[#1B5AFF]" />
            </div>
            <div>
              <p className="text-[#5B6472] text-sm">Current MRR</p>
              <p className="text-2xl font-bold text-[#0A0F1E]">€{analytics?.currentMRR?.toLocaleString() || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#00C9A7]/20 flex items-center justify-center">
              <Target className="w-6 h-6 text-[#00C9A7]" />
            </div>
            <div>
              <p className="text-[#5B6472] text-sm">Conversion Rate</p>
              <p className="text-2xl font-bold text-[#0A0F1E]">{analytics?.conversionRate || 0}%</p>
            </div>
          </div>
        </Card>

        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-[#8B5CF6]" />
            </div>
            <div>
              <p className="text-[#5B6472] text-sm">Total Users</p>
              <p className="text-2xl font-bold text-[#0A0F1E]">{analytics?.totalUsers || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#F0B429]/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-[#F0B429]" />
            </div>
            <div>
              <p className="text-[#5B6472] text-sm">Total Records</p>
              <p className="text-2xl font-bold text-[#0A0F1E]">{analytics?.totalRecords || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* User Growth */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0A0F1E] mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#1B5AFF]" />
            User Growth
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics?.userGrowth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF5" />
              <XAxis dataKey="month" stroke="#5B6472" fontSize={12} />
              <YAxis stroke="#5B6472" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E8EDF5',
                  borderRadius: '8px'
                }} 
              />
              <Legend />
              <Line type="monotone" dataKey="employers" stroke="#1B5AFF" strokeWidth={2} name="Employers" />
              <Line type="monotone" dataKey="employees" stroke="#00C9A7" strokeWidth={2} name="Employees" />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Revenue Growth */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0A0F1E] mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#00C9A7]" />
            Monthly Revenue
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics?.revenueGrowth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF5" />
              <XAxis dataKey="month" stroke="#5B6472" fontSize={12} />
              <YAxis stroke="#5B6472" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E8EDF5',
                  borderRadius: '8px'
                }}
                formatter={(value) => [`€${value}`, 'Revenue']}
              />
              <Bar dataKey="revenue" fill="#1B5AFF" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Additional Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Distribution */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0A0F1E] mb-4">User Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analytics?.userDistribution || []}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {(analytics?.userDistribution || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        {/* Record Status Distribution */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0A0F1E] mb-4">Record Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analytics?.recordDistribution || []}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {(analytics?.recordDistribution || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminAnalytics;
